// components/booking/BookingStepService.js
import React, { useState, useEffect } from 'react';
import { Check, ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

// Import the services data
const servicesData = {
  // Full Detail Packages
  full: [
    {
      id: 'silver',
      title: 'Silver Full Detail',
      tagline: 'Refresh',
      taglineColor: 'orange-400', 
      description: 'Our Refresh Package. Best for maintenance or quick refresh.',
      price: 195,
      features: {
        'INTERIOR': [
          'Interior Vacuum',
          'Wipe all surfaces',
          'Stains (Spot Treatment)',
          'Windows & Mirrors',
          'Door Jambs',
          'Floor Mats',
          'Detail Trunk'
        ],
        'EXTERIOR': [
          'Quick Hand Wash',
          'Clean Rims & Tires',
          'Wax Protection (3 Months)'
        ]
      }
    },
    {
      id: 'gold',
      title: 'Gold Full Detail',
      tagline: 'Most Popular',
      taglineColor: 'orange-400',
      description: 'Our Standard Package. A very thorough inside-out detail.',
      price: 275,
      features: {
        'INTERIOR': [
          'Double Vacuum Interior',
          'Wipe all surfaces',
          'Stains (Spot Treatment)',
          'Windows & Mirrors',
          'Clean & Protect Plastic',
          'Detail Floor Mats and Shine',
          'Detail Trunk & Door Jambs'
        ],
        'EXTERIOR': [
          'Spot Polish',
          'Professional Hand Wash',
          'Clay Bar Exterior',
          'Detail Rims & Tires',
          'Wheel Wells',
          'Wax Protection (3 Months)'
        ]
      }
    },
    {
      id: 'diamond',
      title: 'Diamond Full Detail',
      tagline: 'VIP',
      taglineColor: 'yellow-400',
      description: 'Ultimate Detail Experience. Includes Full Polish & Shampoo/Extraction.',
      price: 399,
      features: {
        'INTERIOR': [
          'Shampoo Seats & Carpet',
          'Double Vacuum Interior',
          'Wipe all Surfaces',
          'Stain (Spot Treatment)',
          'Clean & Protect Plastic',
          'Windows & Mirrors',
          'Detail Floor Mats and Shine',
          'Detail Trunk'
        ],
        'EXTERIOR': [
          'Premium 1-Step Polish',
          'Professional Hand Wash',
          'Clay Bar Exterior',
          'Wash Wheel Wells',
          'Dress Trims/Tires',
          'Clean Door Jams',
          'Wax Protection (3 Months)'
        ]
      }
    }
  ],
  
  // Exterior Only Packages
  exterior: [
    {
      id: 'classic_exterior',
      title: 'Classic Exterior',
      tagline: 'Classic',
      taglineColor: 'orange-400',
      description: 'Our Classic Package. Best for maintenance or quick refresh.',
      price: 168,
      features: [
        'Basic Spot Polish',
        'Professional Hand Wash',
        'Clay Bar Exterior',
        'Wheel Wells',
        'Detail Rim & Tires',
        'Window & Mirrors',
        'Wax Protection (3 Months)'
      ]
    },
    {
      id: 'wax_polish',
      title: 'Wax & Polish',
      tagline: 'Popular',
      taglineColor: 'orange-400',
      description: 'This Package will remove light swirls and scratches.',
      price: 289,
      features: [
        '1-Step Paint Correction Polish',
        'Professional Hand Wash',
        'Clay Bar Exterior',
        'Wheel Wells',
        'Detail Rim & Tires',
        'Window & Mirrors',
        'Wax Protection (3 Months)'
      ]
    },
    {
      id: 'cut_polish',
      title: 'Cut & Polish',
      tagline: 'VIP',
      taglineColor: 'yellow-400',
      description: 'This Package will remove deep swirls and scratches.',
      price: 439,
      features: [
        '2-Step Paint Correction Polish',
        'Professional Hand Wash',
        'Clay Bar Exterior',
        'Wheel Wells',
        'Detail Rim & Tires',
        'Window & Mirrors',
        'Wax Protection (3 Months)'
      ]
    }
  ],
  
  // Interior Only Packages
  interior: [
    {
      id: 'classic_interior',
      title: 'Classic Interior',
      tagline: 'Classic',
      taglineColor: 'orange-400',
      description: 'Good for maintaining your vehicle interior.',
      price: 186,
      features: [
        'Complete Interior Vacuum',
        'Wipe All Surface',
        'Stains (Spots Treatment)',
        'Windows & Mirrors',
        'Detail Floor Mats and Shine',
        'Detail Trunk'
      ]
    },
    {
      id: 'comfort_interior',
      title: 'Comfort Interior',
      tagline: 'Most Popular',
      taglineColor: 'orange-400',
      description: 'Enhanced interior cleaning with extra attention to detail.',
      price: 239,
      features: [
        'Double Interior Vacuum',
        'Complete Surface Cleaning',
        'Stain Treatment & Spot Cleaning',
        'Clean & Protect Plastic Surfaces',
        'Windows & Mirrors Polishing',
        'Deep Clean Door Panels & Jambs',
        'Detail Floor Mats and Apply Protectant',
        'Complete Trunk Detailing'
      ]
    },
    {
      id: 'deep_clean',
      title: 'Deep Clean',
      tagline: 'Premium',
      taglineColor: 'yellow-400',
      description: 'Includes seat & carpet shampoo and conditioner.',
      price: 289,
      features: [
        'Deep Cleaning Treatment',
        'Shampoo All Carpets & Seats',
        'Double Vacuum Interior',
        'Stain Extraction',
        'Clean & Protect All Plastic',
        'Windows & Mirrors',
        'Detail Floor Mats and Shine',
        'Detail Trunk'
      ]
    }
  ],
  
  // Ceramic Coating Packages
  ceramic: [
    {
      id: 'ceramic_standard',
      title: 'Standard Ceramic Coating',
      tagline: 'Basic',
      taglineColor: 'orange-400',
      description: 'Basic ceramic protection with 1-year durability.',
      price: 599,
      features: [
        'Single layer of ceramic coating',
        'Basic paint preparation',
        'Exterior surfaces only',
        '6-month inspection',
        '1-year durability protection',
        'Professional Hand Wash',
        'Clay Bar Treatment'
      ]
    },
    {
      id: 'ceramic_premium',
      title: 'Premium Ceramic Coating',
      tagline: 'Most Popular',
      taglineColor: 'blue-500',
      description: 'Enhanced ceramic protection with 3-year durability.',
      price: 899,
      features: [
        'Two layers of ceramic coating',
        'One-step paint correction',
        'Wheels and trim protection',
        'Annual inspections',
        '3-year durability protection',
        'Professional Hand Wash',
        'Clay Bar & Polish Treatment'
      ]
    },
    {
      id: 'ceramic_elite',
      title: 'Elite Ceramic Coating',
      tagline: 'VIP',
      taglineColor: 'yellow-400',
      description: 'Ultimate ceramic protection with 5-year durability.',
      price: 1499,
      features: [
        'Three layers of top-tier coating',
        'Multi-stage paint correction',
        'Complete vehicle protection',
        'Bi-annual inspections & maintenance',
        '5-year durability protection',
        '2-Step Paint Correction Polish',
        'Premium Surface Preparation'
      ]
    }
  ]
};

const BookingStepService = ({ 
  serviceDetails, 
  setServiceDetails, 
  onNextStep, 
  onResetService,
  onPriceChange = null
}) => {
  const [activeTab, setActiveTab] = useState('full');
  const location = useLocation();
  
  // REMOVE THE PROBLEMATIC useEffect
  
  // Handle pre-selected service from other pages (like ceramic coating page)
  useEffect(() => {
    if (location.state?.preSelectedService) {
      // Set the active tab based on preSelectedService
      setActiveTab(location.state.preSelectedService);
      
      // If there's a specific service type, select it automatically
      if (location.state.serviceType) {
        const serviceCategory = location.state.serviceCategory || location.state.preSelectedService;
        const selectedService = servicesData[location.state.preSelectedService]?.find(
          service => service.id === location.state.serviceType
        );
        
        if (selectedService) {
          // Instead of immediately setting service details and calling onNextStep,
          // we'll set the service details which will trigger the confirmation modal
          setServiceDetails({
            ...selectedService,
            category: serviceCategory
          });
          
          // Clear the location state to prevent reselection on refreshes
          window.history.replaceState({}, document.title);
        }
      }
    }
  }, [location.state, setServiceDetails]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Get gradient color based on tagline type
  const getGradientColor = (tagline) => {
    if (tagline === 'VIP' || tagline === 'Premium') return 'from-indigo-700 to-blue-900';
    if (tagline === 'Most Popular' || tagline === 'Popular') return 'from-blue-600 to-purple-500';
    return 'from-blue-500 to-cyan-400';
  };

  // Already selected service view
  if (serviceDetails) {
    // We'll no longer show the service details here - they're shown in the confirmation modal
    // Instead, we'll just render the service selection interface
    // This helps maintain consistency in the flow where user always sees confirmation
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-6">
        <p className="mb-6 text-gray-600">
          Please select a detailing package to continue with booking:
        </p>
        
        {/* Service Tabs */}
        <div className="flex flex-wrap mb-8 rounded-full overflow-hidden shadow-md">
          <button 
            onClick={() => handleTabChange('full')}
            className={`flex-1 py-3 px-6 transition-all duration-200 ${
              activeTab === 'full' 
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Full Detail
          </button>
          <button 
            onClick={() => handleTabChange('exterior')}
            className={`flex-1 py-3 px-6 transition-all duration-200 ${
              activeTab === 'exterior' 
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Exterior Only
          </button>
          <button 
            onClick={() => handleTabChange('interior')}
            className={`flex-1 py-3 px-6 transition-all duration-200 ${
              activeTab === 'interior' 
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Interior Only
          </button>
          <button 
            onClick={() => handleTabChange('ceramic')}
            className={`flex-1 py-3 px-6 transition-all duration-200 ${
              activeTab === 'ceramic' 
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Ceramic Coating
          </button>
        </div>
        
        {/* Service Package Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {servicesData[activeTab].map((pkg) => {
            const gradientColors = getGradientColor(pkg.tagline);
            return (
              <div 
                key={pkg.id}
                className="border rounded-xl overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer border-gray-200"
                onClick={() => {
                  // Add category to the service details
                  setServiceDetails({
                    ...pkg,
                    category: activeTab
                  });
                }}
              >
                <div className={`bg-gradient-to-r ${gradientColors} text-white p-4`}>
                  <h3 className="text-xl font-semibold">{pkg.title}</h3>
                  <p className="mt-1 text-white/80">{pkg.description}</p>
                </div>
                <div className="p-4">
                  <ul className="mb-4">
                    {
                      /* Handle both array and object formats for features */
                      (Array.isArray(pkg.features) ? pkg.features : 
                        // If it's an object with categories, show first 5 features with category labels
                        Object.entries(pkg.features).flatMap(([category, items]) => 
                          items.slice(0, 3).map(item => `${item}`)
                        ).slice(0, 5)
                      ).map((feature, index) => (
                        <li key={index} className="flex items-start mb-2 last:mb-0">
                          <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))
                    }
                    {!Array.isArray(pkg.features) && 
                      Object.values(pkg.features).flat().length > 5 && 
                      <li className="text-sm text-blue-500 ml-7">+ more included</li>
                    }
                  </ul>
                  <div className="mt-auto">
                    <div className="text-2xl font-bold text-gray-900 mb-1">${pkg.price}</div>
                    <p className="text-gray-500 text-sm mb-3">Most sedans and small SUVs</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-6">
          <Link to="/services">
            <button className="text-blue-600 hover:text-blue-800 flex items-center">
              <ArrowLeft className="h-4 w-4 mr-1" />
              View all detailing services
            </button>
          </Link>
        </div>
      </div>
    );
  }

  // Default package selection view with tabs
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-6">
      <p className="mb-6 text-gray-600">
        Please select a detailing package to continue with booking:
      </p>
      
      {/* Service Tabs */}
      <div className="flex flex-wrap mb-8 rounded-full overflow-hidden shadow-md">
        <button 
          onClick={() => handleTabChange('full')}
          className={`flex-1 py-3 px-6 transition-all duration-200 ${
            activeTab === 'full' 
              ? 'bg-blue-500 text-white font-medium'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Full Detail
        </button>
        <button 
          onClick={() => handleTabChange('exterior')}
          className={`flex-1 py-3 px-6 transition-all duration-200 ${
            activeTab === 'exterior' 
              ? 'bg-blue-500 text-white font-medium'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Exterior Only
        </button>
        <button 
          onClick={() => handleTabChange('interior')}
          className={`flex-1 py-3 px-6 transition-all duration-200 ${
            activeTab === 'interior' 
              ? 'bg-blue-500 text-white font-medium'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Interior Only
        </button>
        <button 
          onClick={() => handleTabChange('ceramic')}
          className={`flex-1 py-3 px-6 transition-all duration-200 ${
            activeTab === 'ceramic' 
              ? 'bg-blue-500 text-white font-medium'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Ceramic Coating
        </button>
      </div>
      
      {/* Service Package Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {servicesData[activeTab].map((pkg) => {
          const gradientColors = getGradientColor(pkg.tagline);
          return (
            <div 
              key={pkg.id}
              className="border rounded-xl overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer border-gray-200"
              onClick={() => {
                // Add category to the service details
                setServiceDetails({
                  ...pkg,
                  category: activeTab
                });
              }}
            >
              <div className={`bg-gradient-to-r ${gradientColors} text-white p-4`}>
                <h3 className="text-xl font-semibold">{pkg.title}</h3>
                <p className="mt-1 text-white/80">{pkg.description}</p>
              </div>
              <div className="p-4">
                <ul className="mb-4">
                  {
                    /* Handle both array and object formats for features */
                    (Array.isArray(pkg.features) ? pkg.features : 
                      // If it's an object with categories, show first 5 features with category labels
                      Object.entries(pkg.features).flatMap(([category, items]) => 
                        items.slice(0, 3).map(item => `${item}`)
                      ).slice(0, 5)
                    ).map((feature, index) => (
                      <li key={index} className="flex items-start mb-2 last:mb-0">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))
                  }
                  {!Array.isArray(pkg.features) && 
                    Object.values(pkg.features).flat().length > 5 && 
                    <li className="text-sm text-blue-500 ml-7">+ more included</li>
                  }
                </ul>
                <div className="mt-auto">
                  <div className="text-2xl font-bold text-gray-900 mb-1">${pkg.price}</div>
                  <p className="text-gray-500 text-sm mb-3">Most sedans and small SUVs</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-6">
        <Link to="/services">
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <ArrowLeft className="h-4 w-4 mr-1" />
            View all detailing services
          </button>
        </Link>
      </div>
    </div>
  );
};

export default BookingStepService;